import { ContextMenu, SkeletonLoader } from '@payaca/ambrosia';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useMemo, useState } from 'react';

import projectKeys from '@/api/queries/project/keyFactory';
import { useSelector } from '@/api/state';
import DealTemplateModal, {
  DealTemplateModalViews,
} from '@/ui/components/dealTemplateModal/DealTemplateModal';
import EntityTagControl from '@/ui/components/entityTagControl/EntityTagControl';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import DeleteProjectModal from '@/ui/pages/projectPage/components/ProjectHeader/DeleteProjectModal';
import EditProjectModal from '@/ui/pages/projectPage/components/ProjectHeader/EditProjectModal';
import PipelineControl from '@/ui/pages/projectPage/components/ProjectHeader/PipelineControl';
import PipelineStageControl from '@/ui/pages/projectPage/components/ProjectHeader/PipelineStageControl';
import ProjectOwnerControl from '@/ui/pages/projectPage/components/ProjectHeader/ProjectOwnerControl';
import RestoreProjectModal from '@/ui/pages/projectPage/components/ProjectHeader/RestoreProjectModal';

import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { JobPermissions } from '@payaca/permissions/jobs/job.permissions';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import * as dealTemplateActions from '@payaca/store/dealTemplates/dealTemplateActions';
import { TaggableEntityType } from '@payaca/types/tagTypes';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import useGetProjectByIdOverview from '../../../../api/queries/project/useGetProjectByIdOverview';
import { getUserRoles } from '../../../../utils/stateAccessors';

const ProjectHeader: FC = () => {
  const dispatch = useDispatch();
  const userRoles = useSelector(getUserRoles);

  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId: string }>();
  const { project, isLoading: isProjectDataLoading } =
    useGetProjectByIdOverview(projectId);

  // *start*
  // ToDo: remove use of redux in this component
  useEffect(() => {
    // fetch deal templates with empty search to see if any exist on account
    dispatch(dealTemplateActions.requestGetDealTemplates(''));
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);
  const dealTemplates = useSelector(
    (state: any) => state.dealTemplates.dealTemplates
  );
  // *end*

  const [showDealTemplateModal, setShowDealTemplateModal] = useState<
    DealTemplateModalViews | undefined
  >();
  const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);
  const [isDeleteProjectModalOpen, setIsDeleteProjectModalOpen] =
    useState(false);
  const [isRestoreProjectModalOpen, setIsRestoreProjectModalOpen] =
    useState(false);

  const projectContextMenuOptions = useMemo(() => {
    const options = [];
    if (
      userHasRequiredPermission(userRoles, [DealsPermissions.CREATE_TEMPLATE])
    ) {
      options.push({
        label: 'Create template',
        onClick: () => {
          setShowDealTemplateModal(DealTemplateModalViews.CREATE);
        },
      });
    }

    if (project?.archivedAt) {
      options.push({
        label: 'Restore Project',
        onClick: () => {
          setIsRestoreProjectModalOpen(true);
        },
      });
    } else {
      if (
        userHasRequiredPermission(userRoles, [
          DealsPermissions.UPDATE_DEAL_DESCRIPTION,
          DealsPermissions.UPDATE_DEAL_REFERENCE,
        ])
      ) {
        options.push({
          label: 'Edit reference/description',
          onClick: () => setIsEditProjectModalOpen(true),
        });
      }

      if (
        userHasRequiredPermission(userRoles, [
          DealsPermissions.APPLY_TEMPLATE,
        ]) &&
        dealTemplates.length
      ) {
        options.push({
          label: 'Load template',
          onClick: () => {
            setShowDealTemplateModal(DealTemplateModalViews.LOAD);
          },
        });
      }

      if (
        userHasRequiredPermission(userRoles, [
          DealsPermissions.ARCHIVE_DEAL,
          DealsPermissions.ARCHIVE_MY_DEAL,
        ])
      ) {
        options.push({
          label: 'Delete Project',
          onClick: () => {
            setIsDeleteProjectModalOpen(true);
          },
        });
      }
    }

    return options;
  }, [project, dealTemplates, userRoles]);

  if (isProjectDataLoading) {
    return (
      <>
        <div className="mb-4">
          <SkeletonLoader.Title className="mb-4 w-1/4" />

          <div className="flex gap-4">
            <SkeletonLoader.Title />
            <SkeletonLoader.Title />
            <SkeletonLoader.Title />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="typography:prose flex flex-col-reverse sm:flex-row sm:items-center sm:gap-4">
        <h2 className="order-1 sm:order-first">
          Project: #{project?.reference}
        </h2>
        {project?.customer && (
          <h2 className="order-first sm:order-1">
            <Link to={`/customers/${project.customer?.publicId}`}>
              {project.customer.name}
            </Link>
          </h2>
        )}
        <div className="order-2 ml-auto">
          {project && (
            <EntityTagControl
              entityId={+project.id}
              entityType={TaggableEntityType.DEAL}
              entityTags={
                project?.tags.map((tag) => ({
                  id: +tag.id,
                  tagText: tag.name,
                  tagColour: tag.colour || '',
                  isSystemManaged: false,
                  archivedAt: null,
                })) || []
              }
              isReadOnly={project?.archivedAt !== null}
              onEntityTagsChange={() => {
                void queryClient.invalidateQueries({
                  queryKey: projectKeys.overview(projectId),
                });
              }}
            />
          )}
        </div>
      </div>

      {project?.description && (
        <div className="typography:prose">
          <p>{project.description}</p>
        </div>
      )}

      <div className="my-4 flex items-start gap-4 lg:items-stretch">
        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
          {project?.type === 'STANDARD' && (
            <>
              <PermissionGuard
                renderIfHasPermissions={[
                  DealsPermissions.UPDATE_DEAL,
                  DealsPermissions.UPDATE_MY_DEAL,
                ]}
              >
                <div className="min-w-0">
                  <PipelineControl />
                </div>
                <div className="min-w-0">
                  <PipelineStageControl />
                </div>
              </PermissionGuard>

              <PermissionGuard
                renderIfHasPermissions={[
                  JobPermissions.UPDATE_JOB,
                  DealsPermissions.GET_DEAL,
                ]}
                requireAllPermissions={true}
              >
                <div className="min-w-0">
                  <ProjectOwnerControl />
                </div>
              </PermissionGuard>
            </>
          )}
        </div>
        {!!projectContextMenuOptions.length && (
          <ContextMenu
            variant={'white'}
            size={'sm'}
            menuItems={projectContextMenuOptions}
          />
        )}
      </div>

      {project && showDealTemplateModal && (
        <DealTemplateModal
          dealId={+project.id}
          projectPublicId={project.publicId}
          isOpen={!!showDealTemplateModal}
          view={showDealTemplateModal}
          onClose={() => setShowDealTemplateModal(undefined)}
        />
      )}

      <EditProjectModal
        isOpen={isEditProjectModalOpen}
        onClose={() => setIsEditProjectModalOpen(false)}
      />

      <DeleteProjectModal
        isOpen={isDeleteProjectModalOpen}
        onClose={() => setIsDeleteProjectModalOpen(false)}
      />

      <RestoreProjectModal
        isOpen={isRestoreProjectModalOpen}
        onClose={() => setIsRestoreProjectModalOpen(false)}
      />
    </>
  );
};

export default ProjectHeader;
